import "./style.scss";

import { Button } from "@mui/material";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import Plus from "assets/img/svg/plus";
import Upload from "assets/img/svg/upload";
import ImportData from "components/ImportData";
import { buttonStyle } from "components/Management";
import ModalBox from "components/Modal";
import BrandAutocomplete from "components/shared/BrandAutocomplete";
import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import ControlFilter from "components/shared/ControlFilter";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import ModalBase from "components/shared/ModalBase";
import type { INewPaymentData } from "components/shared/PaymentModal";
import PaymentModal from "components/shared/PaymentModal";
import ShowAdvanceStatements from "components/shared/ShowAdvanceStatements";
import SmallSearchBar from "components/shared/SmallSearchBar";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { consumeAdvancePaymentNavigationTarget } from "helpers/advancePaymentNavigation";
import { refetchManagementGeneralTab } from "helpers/refetchManagementGeneralTab";
import { formatDateToIso, getCleanJSON, getCleanStartorEndDay, isObjectEmpty, sortByProperty } from "helpers/utils";
import useAPIPagination, { type TPaidStatus } from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePageTitle from "hooks/usePageTitle";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import { deleteAdvancePayment, deleteAdvanceSlice, exportAdvancePayments } from "store/advancePaymentSlice";
import { fetchBrands } from "store/brandStore";
import type { IBeneficiaryPayment } from "store/paymentSlice";
import { EPaymentResourceType } from "store/paymentSlice";
import { fetchPeriods } from "store/periodStore";
import { resetStatements } from "store/statementsStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { PaymentStatus } from "store/types";
import type { IDatesRange } from "types";

import variables from "../../assets/styles/_variables.module.scss";
import AdvancePaymentsTable from "./AdvancePaymentsTable";
import DeleteAdvanceDetails from "./DeleteAdvanceDetails";
import ManagementPreviewModal, { type ManagementImpactKind } from "./ManagementPreviewModal";
import NewAdvanceModal from "./NewAdvanceModal";
import { parseStoredAdvanceDateRange } from "./utils/advanceDateRange";

export interface IAdvancePageData {
    dateRange?: { startDate: string; endDate: string };
}

type AdvanceEmptyStateType = "advancePayment" | "advancePaid" | "advanceToPay" | "advanceUnpaid";

function getAdvanceEmptyStateType(paidStatus: TPaidStatus): AdvanceEmptyStateType {
    switch (paidStatus) {
        case "paid":
            return "advancePaid";
        case "topay":
            return "advanceToPay";
        case "unpaid":
            return "advanceUnpaid";
        default:
            return "advancePayment";
    }
}

const AdvanceManagement = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    usePageTitle(t("AdvancePayment.Heading"));

    const { advanceSlices, allAdvancesDatesRange } = useAppSelector((state) => state.advancePayment);
    const { periodsDateRange, periods, activePeriod } = useAppSelector((state) => state.period);
    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);

    const [slices, setSlices] = useState(advanceSlices);
    const [elementsForPayment, setElementsForPayments] = useState<INewPaymentData[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [resourcePayment, setResourcePayment] = useState<IBeneficiaryPayment>();
    const [linkedAdvancePayment, setLinkedAdvancePayment] = useState<IAdvanceSlice>();

    const [modalType, setModalType] = useState<"" | "add" | "delete" | "import" | "payment" | "update">("");
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentSlice, setCurrentSlice] = useState<IAdvanceSlice | null>(null);
    const [creationType, setCreationType] = useState<undefined | "advance" | "package">(undefined);
    const [isConnected, setIsConnected] = useState<boolean>(false);

    const [deleteWholeAdvance, setDeleteWholeAdvance] = useState<number | null>(null);
    const [managementPreviewSlice, setManagementPreviewSlice] = useState<IAdvanceSlice | null>(null);
    const [managementPreviewKind, setManagementPreviewKind] = useState<ManagementImpactKind | null>(null);

    const { getCompanySettings, companyInfos } = useCompanySettings();
    const usesMultipleBrand = getCompanySettings("uses_multiple_brands");
    const { brands } = useAppSelector((state) => state.brands);
    const { periodsFetchStatus } = useAppSelector((state) => state.period);

    // sessionStorage is user-controlled: a malformed value must not crash the page on render.
    const storedPageData = getCleanJSON(sessionStorage.getItem("advance_page_data") ?? "{}");
    const advancePageData: IAdvancePageData = typeof storedPageData === "object" ? storedPageData : {};

    /**
     * Gets date range for date filter
     */
    const defaultDatesRange = useMemo(() => {
        const storedDateRange = parseStoredAdvanceDateRange(advancePageData["dateRange"]);
        if (storedDateRange) return storedDateRange;
        if (!allAdvancesDatesRange?.startDate) {
            if (!periodsDateRange || isObjectEmpty(periodsDateRange)) return;
            const firstPeriodStart = getCleanStartorEndDay(periodsDateRange.startDate);
            const lastPeriodEnd = getCleanStartorEndDay(periodsDateRange.endDate, { time: "end" });

            return { startDate: firstPeriodStart, endDate: lastPeriodEnd };
        }
        return allAdvancesDatesRange;
    }, [allAdvancesDatesRange, periodsDateRange]);

    const {
        query,
        limitPagination,
        page,
        count,
        handleQuery,
        handleChangePage,
        handleChangeLimit,
        handleRequest,
        setChosenBrand,
        paidStatus,
        setPaidStatus,
        isForfait,
        setIsForfait,
        customStatusId,
        setCustomStatusId,
        setPage,
        setOffsetPagination,
        handleSort,
        setDates,
        dates,
        requestStatus,
        sortBy,
        sortOrder,
    } = useAPIPagination({
        type: "advancePayment",
        sortBy: "id",
        sortOrder: "desc",
    });

    const APIData = {
        limitPagination,
        page,
        count,
        handleChangePage,
        handleChangeLimit,
        handleQuery,
        handleSort,
        sortBy,
        sortOrder,
        requestStatus,
    };

    const useStateFunctions = {
        setCurrentSlice,
        setResourcePayment,
        setLinkedAdvancePayment,
        setSelectedRows,
        setModalType,
        setIsDrawerOpen,
        setCreationType,
        setIsConnected,
    };
    const useStateData = { currentSlice, selectedRows, creationType };

    /**
     * @Note about deleteWholeAdvance -> is used to delete the whole advance payment.
     * For forfait, we always delete the whole advance payment. Because one term can have multiple forfait advance payments.
     * For advance, we delete all related termId advance payments related to the slice.
     * The logic is not optimal, this need a little refac later
     */
    const handleDeleteAdvancePayment = async () => {
        if (!currentSlice) return;
        let response: any;
        if (currentSlice.isForfait && !!deleteWholeAdvance) {
            response = await dispatch(
                deleteAdvancePayment({
                    id: currentSlice.paymentId,
                }),
            );
        } else {
            response = await dispatch(
                deleteAdvanceSlice({
                    termId: currentSlice.termId,
                    sliceId: currentSlice.id,
                    deleteWholeAdvance: !!deleteWholeAdvance,
                }),
            );
        }

        if (response.meta.requestStatus === "fulfilled") {
            setModalType("");
            handleRequest();
            await updatePaymentPageState();
            void refetchManagementGeneralTab(dispatch, activePeriod);
        }
    };

    const toggleDrawer =
        (anchor: any, open: any): any =>
        (event: any) => {
            if (
                event &&
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            setIsDrawerOpen(open);
            dispatch(resetStatements());
        };

    const handleExport = () => {
        if (!dates?.startDate || !dates.endDate) {
            dispatch(exportAdvancePayments());
            return;
        }

        dispatch(
            exportAdvancePayments({
                startDate: formatDateToIso(dates.startDate),
                endDate: formatDateToIso(dates.endDate),
            }),
        );
    };

    useEffect(() => {
        if (usesMultipleBrand && !brands.length) dispatch(fetchBrands(true));
    }, [usesMultipleBrand, brands]);

    const paymentFilterOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: "paid", label: t("Filters.Paid") },
        { value: "topay", label: t("Filters.ForTransfer") },
        { value: "unpaid", label: t("Filters.Unpaid") },
    ];

    const customStatuses = useAppSelector((state) => state.advanceCustomStatuses.items);
    const customStatusOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: "none", label: t("AdvancePayment.NoCustomStatus") },
        ...customStatuses.map((status) => ({ value: String(status.id), label: status.name })),
    ];

    const advanceTypeOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: "0", label: t("Filters.Advance") },
        { value: "1", label: t("Filters.Forfait") },
    ];

    const menuOptions: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("Settings.ExportAdvancePayments"),
            icon: <DownloadOutline width="16" height="16" />,
            action: handleExport,
        },
    ];

    useEffect(() => {
        if (periodsFetchStatus === "idle") dispatch(fetchPeriods());
    }, [dispatch]);

    useEffect(() => {
        if (!advanceSlices) return;
        setSlices(advanceSlices);
    }, [advanceSlices]);

    const hasOpenedNavigatedPayment = useRef(false);

    useEffect(() => {
        if (hasOpenedNavigatedPayment.current || requestStatus === "pending" || slices.length === 0) return;

        const paymentId = consumeAdvancePaymentNavigationTarget();
        if (paymentId == null) return;

        const slice = slices.find((s) => s.paymentId === paymentId);
        hasOpenedNavigatedPayment.current = true;
        if (!slice) return;

        const paymentSlices = slices.filter((item) => item.paymentId === paymentId);
        setLinkedAdvancePayment({ ...slice, slices: paymentSlices });
        setCurrentSlice(slice);
        setCreationType(slice.isForfait ? "package" : "advance");
        setModalType("update");
        setIsConnected(!!slice.connectedTPD);
    }, [slices, requestStatus]);

    /**
     * Opens payment modal and sets info such as left to pay and id for each beneficiary selected.
     */
    const handleOpenPaymentModal = (slice?: IAdvanceSlice) => {
        let elementsData: INewPaymentData[] = [];
        if (selectedRows.length > 0) {
            elementsData = slices
                .filter((item) => selectedRows.includes(item.id) && item?.status === PaymentStatus.NOT_PAID)
                .map((item) => {
                    return {
                        amount: +(item.netToPay ?? 0),
                        id: item.id,
                        name: `${item.author} - ${item.title}`,
                        iban: item.iban,
                        bankInfoValid: !!item?.validBankInfos,
                    };
                });
        } else if (slice) {
            elementsData = [
                {
                    amount: +(slice.netToPay ?? 0),
                    id: slice.id,
                    bankInfoValid: !!slice.validBankInfos,
                    iban: slice.iban,
                    name: slice.author,
                    beneficiaryId: slice.beneficiaryId,
                },
            ];
        }
        setElementsForPayments(elementsData);
        setModalType("payment");
    };

    const handleOpenManagementPreviewModal = (slice: IAdvanceSlice, impactKind: ManagementImpactKind) => {
        setManagementPreviewSlice(slice);
        setManagementPreviewKind(impactKind);
    };

    const handleCloseManagementPreviewModal = () => {
        setManagementPreviewSlice(null);
        setManagementPreviewKind(null);
    };

    /**
     * Handles opening creation modal
     * whether it is an advance or a package.
     */
    const handleCreationModal = (open: boolean, type?: "advance" | "package") => {
        if (open && type) {
            setLinkedAdvancePayment(undefined);
            setIsConnected(false);
            setCreationType(type);
            setModalType("add");
        } else {
            setCreationType(undefined);
            setModalType("");
        }
    };

    const newPaymentActions: Action[] = [
        {
            name: t("AdvancePayment.AddAdvance"),
            action: () => handleCreationModal(true, "advance"),
            rightName: "",
            subtitle: t("AdvancePayment.AdvanceDescription"),
        },
        {
            name: t("AdvancePayment.AddPackage"),
            action: () => handleCreationModal(true, "package"),
            rightName: "",
            subtitle: t("AdvancePayment.FlatFeeDescription"),
        },
    ];

    const handleDatesChange = (updatedDates: IDatesRange | undefined) => {
        if (!updatedDates) return;
        setDates(updatedDates);
        advancePageData["dateRange"] = {
            startDate: formatDateToIso(updatedDates.startDate),
            endDate: formatDateToIso(updatedDates.endDate),
        };
        sessionStorage.setItem("advance_page_data", JSON.stringify(advancePageData));
    };

    /**
     * Check if we should disable the pay button at select bulk advances
     * @returns boolean
     */
    const disableBulkPayBtn = () => {
        if (!selectedRows.length) return;
        const slicesMap = new Map(slices.map((slice) => [slice.id, slice]));
        const hasEverPaidAdvances = selectedRows.some((id) => {
            const slice = slicesMap.get(id);
            return slice?.status === PaymentStatus.PAID || slice?.status === PaymentStatus.TO_PAY;
        });
        return hasEverPaidAdvances;
    };

    const isTableLoading = requestStatus === "pending" || (requestStatus === "idle" && slices.length === 0);

    const emptyStateType = getAdvanceEmptyStateType(paidStatus);

    return (
        <div className="advance-management-wrapper">
            <ModalBase
                open={modalType === "payment"}
                onClose={() => setModalType("")}
                modalTitle={
                    selectedRows.length > 0
                        ? t("PartialPayment.BulkPaymentModalTitle", { element: t("BPStatement.Advance").toLowerCase() })
                        : t("PartialPayment.PaymentModalTitle", { name: currentSlice?.author })
                }
            >
                <PaymentModal
                    elementsData={elementsForPayment}
                    type={EPaymentResourceType.ADVANCE_PAYMENT}
                    handleClose={() => setModalType("")}
                    hasSeveralPayments={selectedRows.length > 0}
                />
            </ModalBase>
            {isDrawerOpen && (
                <ShowAdvanceStatements isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} slice={currentSlice} />
            )}
            <header className="advance-management-header">
                <div className="advance-heading">
                    <h1 className="heading">{t("AdvancePayment.Heading")}</h1>
                    <ExpandedHelpdeskIcon
                        link="https://helpdesk.crealo.app/fr/article/comprendre-lecran-de-gestion-des-a-valoir-95cho6/"
                        text={t("Documentation.AdvancePage")}
                    />
                </div>
                <div className="buttons-group-two">
                    <ButtonMenu
                        mainActions={{ icon: <Plus />, name: t("New") }}
                        actions={newPaymentActions}
                        showExpandBtn={false}
                        wrapSubtitles
                    />
                    <StyledButton
                        onClick={() => setModalType("import")}
                        buttonType="secondary"
                        style={{ ...buttonStyle }}
                        sx={{
                            gap: "8px",
                        }}
                    >
                        <Upload />
                        {t("AdvancePayment.ImportData")}
                    </StyledButton>
                    <ThreeDotMenu options={menuOptions} />
                </div>
            </header>
            <main className="advance-management-main">
                {selectedRows.length > 0 ? (
                    <div className="selected-rows-wrapper">
                        <div className="selected-rows-counter">{selectedRows.length}</div>
                        <Button
                            sx={{
                                backgroundColor: "white",
                                color: variables["primary-purple-dark"],
                                textTransform: "none",
                                fontFamily: "Poppins",
                            }}
                            onClick={() => handleOpenPaymentModal()}
                            disabled={disableBulkPayBtn()}
                        >
                            {t("AdvancePayment.Pay")}
                        </Button>
                    </div>
                ) : (
                    <div className="control-group">
                        <SmallSearchBar filter={query} onSearch={handleQuery} />
                        {usesMultipleBrand && (
                            <BrandAutocomplete
                                brands={brands}
                                setChosenBrand={setChosenBrand}
                                companyName={companyInfos?.name}
                                className="brand-filter"
                                asColumn
                            />
                        )}
                        <ControlFilter
                            filterLabel={t("Filters.PaymentStatus")}
                            filterOptions={paymentFilterOptions}
                            controlValue={paidStatus}
                            controlLabel={paymentFilterOptions.find((option) => option.value === paidStatus)?.label}
                            onValueChange={(value) => setPaidStatus(value)}
                            selector
                            asColumn
                            minWidth="11rem"
                        />
                        {customStatuses.length > 0 && (
                            <ControlFilter
                                filterLabel={t("Filters.AdvanceCustomStatus")}
                                filterOptions={customStatusOptions}
                                controlValue={customStatusId}
                                controlLabel={
                                    customStatusOptions.find((option) => option.value === customStatusId)?.label
                                }
                                onValueChange={(value: string) => {
                                    setPage(0);
                                    setOffsetPagination(0);
                                    setCustomStatusId(value);
                                }}
                                selector
                                asColumn
                                minWidth="11rem"
                                menuMaxHeight="280px"
                            />
                        )}
                        <ControlFilter
                            filterLabel={t("Type_colon")}
                            filterOptions={advanceTypeOptions}
                            controlValue={isForfait}
                            onValueChange={(value) => setIsForfait(value)}
                            asColumn
                        />
                        <DateRangeSelectors
                            label={t("PaymentDate_colon")}
                            dateRange={allAdvancesDatesRange?.startDate ? allAdvancesDatesRange : defaultDatesRange}
                            defaultValues={defaultDatesRange}
                            handleChanges={handleDatesChange}
                            asColumn
                            loading={isTableLoading}
                        />
                    </div>
                )}

                <div className="table-wrapper">
                    <AdvancePaymentsTable
                        APIData={APIData}
                        slices={[...slices]}
                        useStateFunctions={useStateFunctions}
                        useStateData={useStateData}
                        handleOpenPaymentModal={handleOpenPaymentModal}
                        handleOpenManagementPreviewModal={handleOpenManagementPreviewModal}
                        isLoading={isTableLoading}
                        emptyStateType={emptyStateType}
                        onEmptyStateControlChange={setPaidStatus}
                    />
                </div>
            </main>
            {["add", "update"].includes(modalType) && (
                <NewAdvanceModal
                    openModal={["add", "update"].includes(modalType)}
                    onClose={(result) => {
                        if (result?.saved) handleRequest({ silent: true });
                        handleCreationModal(false);
                    }}
                    from="advancePage"
                    type={creationType}
                    setLinkedAdvancePayment={setLinkedAdvancePayment}
                    linkedAdvancePayment={linkedAdvancePayment}
                    isUpdate={modalType === "update"}
                    isConnected={isConnected}
                />
            )}
            {currentSlice && (
                <DeleteAdvanceDetails
                    sliceData={currentSlice}
                    resourceData={resourcePayment}
                    open={modalType === "delete"}
                    onClose={() => {
                        setModalType("");
                    }}
                    onDelete={handleDeleteAdvancePayment}
                    deleteWholeAdvance={deleteWholeAdvance}
                    setDeleteWholeAdvance={setDeleteWholeAdvance}
                />
            )}
            <ManagementPreviewModal
                open={managementPreviewSlice != null && managementPreviewKind != null}
                onClose={handleCloseManagementPreviewModal}
                slice={managementPreviewSlice}
                impactKind={managementPreviewKind}
                periods={periods}
                advanceSlices={slices}
            />
            <ModalBox
                open={modalType === "import"}
                dictionaryKey="advancePayments"
                onClose={() => setModalType("")}
                mainTitle={t("ImportData.ImportAdvancePayments")}
            >
                <ImportData
                    dictionaryKey="advancePayments"
                    close={() => setModalType("")}
                    onSucceed={() => {
                        handleRequest();
                        void updatePaymentPageState();
                        void refetchManagementGeneralTab(dispatch, activePeriod);
                    }}
                />
            </ModalBox>
        </div>
    );
};

export default AdvanceManagement;
